import { useMemo, useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import {
  OverlayContainer,
  useSortable,
  useSortableDefaults,
} from '@components/drag-and-drop/defaults';
import { PortalDragOverlay } from '@components/drag-and-drop/portal-overlay';
import { createForm, useCreateFormStore } from '@components/form';
import { SmallDragHandle } from '@components/form-builder/drag-handle';
import { FormInstance } from '@components/form/create-form';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconLabel } from '@components/icons/label';
import { IconTrash } from '@components/icons/trash';
import { IconWorkType } from '@components/icons/work-type';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  CUSTOM_FIELD_PREFIX,
  generateFieldName,
  getFormEndpointVariables,
  getVariableCount,
  useFieldTypes,
} from './common';
import { getFieldIds } from './endpoint-fields';
import { IntegrationEndpointVariable } from './integrations-types';
import { EditCustomResponseValueSidebar } from './sidebars/edit-custom-response-value';
import { Field, Grid } from './styles';

export const visibleColumns = {
  name: true,
  type: true,
  label: true,
  example: false,
  default: false,
  visible: false,
  required: false,
  format: true,
};

export const CustomValuesGrid = styled(Grid).attrs({
  $numColumns: 3,
  $numSmallColumns: 1,
})({
  [`& > ${Field}`]: {
    alignItems: 'center',
  },
  [`${Field}:nth-last-child(-n+4)`]: {
    borderBottom: `1px solid ${theme.color.uiTrans10}`,
  },
});

export const SortableCustomRow = <T,>({
  field,
  form: Form,
  typeOptions,
  onRemove,
  onEdit,
  isDragDisabled,
}: {
  field: IntegrationEndpointVariable;
  form: FormInstance<T>;
  typeOptions: ReturnType<typeof useFieldTypes>;
  onRemove: (name: string) => void;
  onEdit: () => void;
  isDragDisabled?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const { attributes, listeners, setNodeRef, style } = useSortable({
    id: field.name,
    disabled: isDragDisabled,
  });

  const { typeId, labelId, jqQueryId } = getFieldIds(field.name);
  const displayName =
    field.name.split('__').find((part) => part.startsWith(CUSTOM_FIELD_PREFIX)) ?? field.name;

  return (
    <div ref={setNodeRef} style={style}>
      <CustomValuesGrid>
        <Field>
          <SmallDragHandle {...attributes} {...listeners} />
          <ActionText
            font="regular14"
            color="elementIndigo100"
            hoverColor="elementIndigo100"
            onClick={onEdit}
            text={displayName}
            title={displayName}
            ellipsis
          />
          {field.jqQuery && <Form.HiddenField id={jqQueryId} defaultValue={field.jqQuery} />}
        </Field>
        <Field>
          <Form.Select
            label=""
            id={typeId}
            options={typeOptions}
            defaultValue={field.type}
            isClearable={false}
          />
        </Field>
        <Field>
          <Form.TextField id={labelId} label={t('label')} hideLabel defaultValue={field.label} />
        </Field>
        <Field>
          <IconButton
            icon={<IconTrash />}
            onClick={() => onRemove(field.name)}
            variant="ghost-purple"
          />
        </Field>
      </CustomValuesGrid>
    </div>
  );
};

const Form = createForm<Record<string, unknown>>();

type CustomValuesProps = {
  values: IntegrationEndpointVariable[];
  exampleData: object | null;
  isSaving: boolean;
  onSave: (values: IntegrationEndpointVariable[]) => Promise<unknown> | void;
  dataTitle?: string;
  dataDescription?: string;
};

/**
 * Custom (jq-computed) values for a value list. Returns the in-card `section`
 * and the `sidebar` separately so the caller renders the sidebar outside the
 * collapse card, like the endpoint response-values card does. Custom values are
 * the ones prefixed with the custom marker; the rest of `values` is merged back
 * on save.
 */
export const useCustomValues = ({
  values,
  exampleData,
  isSaving,
  onSave,
  dataTitle,
  dataDescription,
}: CustomValuesProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const hasUnsavedChanges = formStore.useIsDirty();
  const showDeleteModal = useDeleteModal();
  const typeOptions = useFieldTypes();

  const [editingValue, setEditingValue] = useState<IntegrationEndpointVariable | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Stable references so useSortableDefaults' items effect doesn't loop.
  const customs = useMemo(
    () => values.filter((value) => value.name.startsWith(CUSTOM_FIELD_PREFIX)),
    [values]
  );
  const others = useMemo(
    () => values.filter((value) => !value.name.startsWith(CUSTOM_FIELD_PREFIX)),
    [values]
  );

  const {
    items: sortableCustoms,
    itemIds: customIds,
    activeItem: activeCustom,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    strategy,
  } = useSortableDefaults({
    items: customs,
    getId: (item) => item.name,
    onDragEnd: (reordered) => {
      // Fire-and-forget: the mutation's onError shows the failure and refetches
      // the true order, so just swallow the rejection here.
      void Promise.resolve(onSave([...others, ...reordered])).catch(() => {});
    },
  });

  const handleSaveInline = async () => {
    const processedCustoms = getFormEndpointVariables({
      formStore,
      endpointVariables: customs,
      visibleColumns,
    });
    await onSave([...others, ...processedCustoms]);
    formStore.actions.markAsClean();
  };

  const handleRemove = (name: string) => {
    showDeleteModal({
      itemTitle: t('customValue'),
      onConfirm: () => {
        onSave(values.filter((value) => value.name !== name));
      },
    });
  };

  const handleOpenSidebar = (existingValue?: IntegrationEndpointVariable) => {
    if (existingValue) {
      // Seed from the inline form so a pending (unsaved) label/type/jq edit
      // carries into the sidebar instead of the last-saved value.
      const { labelId, typeId, jqQueryId } = getFieldIds(existingValue.name);
      const formValues = formStore.getState().values;
      setEditingValue({
        ...existingValue,
        label: (formValues[labelId] as string) ?? existingValue.label,
        type: (formValues[typeId] as string) ?? existingValue.type,
        jqQuery: (formValues[jqQueryId] as string) ?? existingValue.jqQuery,
      } as IntegrationEndpointVariable);
    } else {
      setEditingValue({
        name: generateFieldName(),
        label: '',
        jqQuery: '',
        type: 'text',
      });
    }
    setIsSidebarOpen(true);
  };

  const handleSaveValue = async (savedValue: IntegrationEndpointVariable) => {
    const exists = customs.some((custom) => custom.name === savedValue.name);
    const nextCustoms = exists
      ? customs.map((custom) => (custom.name === savedValue.name ? savedValue : custom))
      : [...customs, savedValue];
    await onSave([...others, ...nextCustoms]);
  };

  const hasExampleData = exampleData !== null && typeof exampleData === 'object';

  const section = (
    <Form store={formStore} onSubmit={handleSaveInline} space={0}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconFieldCustom color="elementPurple50" />
          <Text text={t('customValues')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={getVariableCount(customs)} font="semibold14" />
        </Stack>
        <Stack orientation="horizontal" space={2} align="center">
          {hasUnsavedChanges && (
            <>
              <Form.Reset
                stopPropagation
                text={t('core.cancel')}
                variant="secondary"
                onClick={formStore.actions.resetStore}
              />
              <Form.Submit
                stopPropagation
                isDisabled={isSaving}
                text={t('core.save')}
                variant="primary-blue"
              />
            </>
          )}
          {hasExampleData && (
            <Button
              text={t('addCustomValue')}
              variant="primary"
              iconLeft={<IconAddPlus />}
              onClick={() => handleOpenSidebar()}
            />
          )}
        </Stack>
      </Stack>
      {customs.length > 0 ? (
        <>
          <CustomValuesGrid>
            <Field>
              <Stack space={1} orientation="horizontal" align="center">
                <IconFieldCustom color="elementPurple50" size={16} />
                <Text text={t('jqQuery')} font="semibold14" color="textSecondary" />
              </Stack>
            </Field>
            <Field>
              <Stack space={1} orientation="horizontal" align="center">
                <IconWorkType color="elementPurple50" size={16} />
                <Text text={t('type')} font="semibold14" color="textSecondary" />
              </Stack>
            </Field>
            <Field>
              <Stack space={1} orientation="horizontal" align="center">
                <IconLabel color="elementPurple50" size={16} />
                <Text text={t('label')} font="semibold14" color="textSecondary" />
              </Stack>
            </Field>
            <Field>
              <div />
            </Field>
          </CustomValuesGrid>
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={customIds} strategy={strategy}>
              {sortableCustoms.map((custom) => (
                <SortableCustomRow
                  key={`${custom.name}-${custom.label}-${custom.type}`}
                  field={custom}
                  form={Form}
                  typeOptions={typeOptions}
                  onRemove={handleRemove}
                  onEdit={() => handleOpenSidebar(custom)}
                  isDragDisabled={isSaving}
                />
              ))}
            </SortableContext>
            <PortalDragOverlay>
              {activeCustom && (
                <OverlayContainer>
                  <CustomValuesGrid>
                    <Field>
                      <SmallDragHandle />
                      <Text text={activeCustom.name} font="regular14" color="elementIndigo100" />
                    </Field>
                    <Field>
                      {typeOptions.find((option) => option.value === activeCustom.type)?.label}
                    </Field>
                    <Field>
                      <Text text={activeCustom.label} font="regular14" />
                    </Field>
                    <Field>
                      <div />
                    </Field>
                  </CustomValuesGrid>
                </OverlayContainer>
              )}
            </PortalDragOverlay>
          </DndContext>
        </>
      ) : (
        <Text
          style={{ marginBottom: pxToRem(16) }}
          font="regular14"
          color="textSecondary"
          text={t('noCustom')}
        />
      )}
    </Form>
  );

  const sidebar = (
    <EditCustomResponseValueSidebar
      key={String(editingValue?.name) + String(isSidebarOpen)}
      value={editingValue}
      exampleData={exampleData}
      dataTitle={dataTitle}
      dataDescription={dataDescription}
      isOpen={isSidebarOpen}
      onClose={() => {
        setIsSidebarOpen(false);
        setEditingValue(null);
      }}
      onSave={handleSaveValue}
    />
  );

  return { section, sidebar };
};

const locale = {
  en: {
    customValues: 'Custom values',
    customValue: 'custom value',
    addCustomValue: 'Add custom value',
    noCustom:
      'No custom values. Click "Add custom value" to create computed values using ./jq queries.',
    type: 'Type',
    label: 'Label',
    jqQuery: './jq query',
  },
};
